import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { isAuthenticated, login } from "@/lib/auth";

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (isAuthenticated()) {
    const target = state?.from?.pathname ?? "/";
    navigate(target, { replace: true });
    return null;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(username, password);
      const target = state?.from?.pathname ?? "/";
      navigate(target, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? "We couldn't sign you in. Check your username and password and try again."
          : "Sign in failed.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-bg px-4">
      {/* Subtle navy bar at top — anchors the brand without dominating */}
      <div
        className="absolute inset-x-0 top-0 h-2 bg-navy"
        aria-hidden="true"
      />

      <div className="mb-8 flex flex-col items-center gap-3">
        <img
          src="/logo-full.png"
          alt="HRIS Workbench"
          className="h-20 w-auto"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <p className="text-xs uppercase tracking-[0.2em] text-slate">
          Built for People · Driven by Purpose
        </p>
      </div>

      <Card className="w-full max-w-sm shadow-card-lg">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            Use your HRIS Workbench account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            <FormField label="Username" required>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </FormField>

            <FormField label="Password" required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </FormField>

            {error && (
              <p
                className="rounded-btn border border-[#FECACA] bg-[#FEE2E2] px-3 py-2 text-sm text-[#B91C1C]"
                role="alert"
              >
                {error}
              </p>
            )}

            <Button type="submit" disabled={pending} className="mt-2">
              {pending ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="mt-6 text-xs text-slate">
        © {new Date().getFullYear()} HRIS Workbench
      </p>
    </div>
  );
}
